import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";
import { Link } from "react-router-dom";
import { Clock, MapPin, AlertCircle, ExternalLink, FileText, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ----------------------- Date helpers -----------------------

// Display order is Monday-first. The weekday integer stored on recurring items
// still follows JavaScript convention (0 = Sunday) so no data migration needed.
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
// displayIndex -> JavaScript Date.getDay() value
const DAY_TO_WEEKDAY = [1, 2, 3, 4, 5, 6, 0];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Local YYYY-MM-DD (no UTC shift). */
function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Return the Monday that starts the current week (local time). */
function startOfWeek(base = new Date()) {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  const day = d.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
  const offset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + offset);
  return d;
}

/** Return an array of 7 Date objects Mon → Sun starting at `start`. */
function weekDays(start) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function shortDate(d) {
  return `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}`;
}

function longRange(start, end) {
  const sameMonth = start.getMonth() === end.getMonth();
  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    return `Week of ${MONTHS[start.getMonth()]} ${start.getDate()}-${end.getDate()}, ${end.getFullYear()}`;
  }
  if (sameYear) {
    return `Week of ${MONTHS[start.getMonth()]} ${start.getDate()} - ${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
  }
  return `Week of ${MONTHS[start.getMonth()]} ${start.getDate()}, ${start.getFullYear()} - ${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
}

// ----------------------- Component -----------------------

export default function CalendarWeekly() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek());
  const [events, setEvents] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [hero, setHero] = useState({ eyebrow: "TIOB Weekly", title: "This week at Temple Israel." });
  const [loading, setLoading] = useState(true);

  const days = useMemo(() => weekDays(weekStart), [weekStart]);
  const start = days[0];
  const end = days[6];

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/events`).catch(() => ({ data: [] })),
      axios.get(`${API}/weekly/recurring`).catch(() => ({ data: [] })),
      axios.get(`${API}/weekly/overrides`, {
        params: { start_date: isoLocal(start), end_date: isoLocal(end) },
      }).catch(() => ({ data: [] })),
      axios.get(`${API}/content/weekly.hero.eyebrow`).catch(() => ({ data: null })),
      axios.get(`${API}/content/weekly.hero.title`).catch(() => ({ data: null })),
    ]).then(([ev, rec, ov, eyebrow, title]) => {
      if (!alive) return;
      setEvents(Array.isArray(ev.data) ? ev.data : []);
      setRecurring(Array.isArray(rec.data) ? rec.data : []);
      setOverrides(Array.isArray(ov.data) ? ov.data : []);
      setHero({
        eyebrow: eyebrow.data?.body || "TIOB Weekly",
        title: title.data?.body || "This week at Temple Israel.",
      });
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => { alive = false; };
  }, [start, end]);

  const byDate = useMemo(() => {
    const map = new Map();
    for (const d of days) map.set(isoLocal(d), []);

    // 1. DB events on those dates
    for (const e of events) {
      if (!e.start_date || !map.has(e.start_date)) continue;
      map.get(e.start_date).push({
        title: e.title,
        time: e.start_time,
        location: e.location,
        description: e.description,
        flyer_url: e.flyer_url,
        rsvp_url: e.rsvp_url,
        payment_url: e.payment_url,
        details_url: e.details_url,
        highlight: !!e.featured,
      });
    }

    // 2. Recurring items (skip inactive)
    days.forEach((d, i) => {
      const key = isoLocal(d);
      const bucket = map.get(key) || [];
      const items = recurring.filter(
        (r) => r.weekday === DAY_TO_WEEKDAY[i] && r.active !== false
      );
      for (const r of items) {
        const dup = bucket.some(
          (x) => (x.title || "").toLowerCase() === (r.title || "").toLowerCase()
        );
        if (!dup) bucket.push(r);
      }
      map.set(key, bucket);
    });

    // 3. One-off overrides on their exact date
    for (const o of overrides) {
      if (!map.has(o.date)) continue;
      map.get(o.date).push(o);
    }

    // 4. Sort each day: display_order asc, then existing order
    for (const [k, arr] of map) {
      arr.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      map.set(k, arr);
    }

    // 5. Enforce single highlight per week (earliest day wins)
    let seenHighlight = false;
    for (const d of days) {
      const arr = map.get(isoLocal(d)) || [];
      for (const it of arr) {
        if (it.highlight) {
          if (seenHighlight) it.highlight = false;
          else seenHighlight = true;
        }
      }
    }

    return map;
  }, [days, events, recurring, overrides]);

  const totalEvents = Array.from(byDate.values()).reduce((n, arr) => n + arr.length, 0);

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  };
  const goToday = () => setWeekStart(startOfWeek());

  return (
    <>
      <SEOHead
        title="Weekly Calendar"
        description="This week at Temple Israel of Brevard - services, classes, gatherings, and meetings. RSVP and registration links inside."
        path="/calendar/weekly"
      />
      <PageHero
        eyebrow={hero.eyebrow}
        title={hero.title}
        subtitle={`${longRange(start, end)} · ${totalEvents} event${totalEvents === 1 ? "" : "s"} this week`}
      />

      <section className="bg-white py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          {/* Week navigation */}
          <div
            className="flex flex-wrap items-center justify-between gap-3 mb-6"
            data-testid="weekly-nav"
          >
            <div className="flex items-center gap-2">
              <button
                onClick={() => shiftWeek(-1)}
                className="btn-ghost text-sm"
                data-testid="weekly-prev"
              >
                ← Previous week
              </button>
              <button
                onClick={goToday}
                className="btn-ghost text-sm"
                data-testid="weekly-today"
              >
                Today
              </button>
              <button
                onClick={() => shiftWeek(1)}
                className="btn-ghost text-sm"
                data-testid="weekly-next"
              >
                Next week →
              </button>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/calendar/month" className="btn-ghost text-sm" data-testid="weekly-full-calendar">
                <Calendar size={14} /> Full Temple Calendar
              </Link>
              <Link to="/calendar/zoom" className="btn-ghost text-sm">
                Zoom info
              </Link>
            </div>
          </div>

          {/* Days */}
          {loading ? (
            <p className="text-center py-16 text-[var(--muted)]">Loading this week&rsquo;s schedule…</p>
          ) : (
            <div className="space-y-4">
              {days.map((d, i) => (
                <DayBlock
                  key={isoLocal(d)}
                  dayName={DAYS[i]}
                  date={shortDate(d)}
                  items={byDate.get(isoLocal(d)) || []}
                  isToday={isoLocal(d) === isoLocal(new Date())}
                />
              ))}
            </div>
          )}

          <p className="text-center mt-12 text-xs text-[var(--muted)]">
            Weekly schedule is populated from the Temple Calendar. To submit an event,
            email{" "}
            <a href="mailto:tioffice@tiofbrevard.com" className="text-[var(--blue-deep)] underline">
              tioffice@tiofbrevard.com
            </a>.
          </p>
        </div>
      </section>
    </>
  );
}

function DayBlock({ dayName, date, items, isToday }) {
  const empty = items.length === 0;
  return (
    <article
      className={`card-soft p-5 md:p-7 bg-white ${empty ? "opacity-70" : ""} ${
        isToday ? "ring-2 ring-[var(--honey)] ring-offset-2 ring-offset-white" : ""
      }`}
      data-testid={`weekly-${dayName.toLowerCase()}`}
    >
      <header className="flex items-baseline justify-between border-b border-[var(--line)] pb-3 mb-5">
        <div className="flex items-baseline gap-4 flex-wrap">
          <p className="font-mono-accent text-xs tracking-[0.28em] uppercase text-[var(--blue-deep)]">
            ★ {dayName}
            {isToday && (
              <span className="ml-2 text-[10px] bg-[var(--honey)] text-[var(--blue-ink)] px-2 py-0.5 rounded-full">
                Today
              </span>
            )}
          </p>
          <p className="font-display text-2xl text-[var(--blue-ink)]">{date}</p>
        </div>
        {empty && (
          <span className="text-xs text-[var(--muted)] font-mono-accent uppercase tracking-widest">
            No events
          </span>
        )}
      </header>

      {items.length > 0 && (
        <div className="space-y-4">
          {items.map((it, i) => (
            <EventItem key={`${dayName}-${i}`} item={it} />
          ))}
        </div>
      )}
    </article>
  );
}

function EventItem({ item }) {
  const wrap = item.highlight
    ? "bg-[var(--blue)]/8 border border-[var(--blue)]/25"
    : item.canceled
    ? "bg-[var(--cream)] border border-[var(--line-warm)]"
    : "bg-[var(--cream-soft)] border border-[var(--line-warm)]";

  const time = item.time || item.start_time || "";

  return (
    <div className={`rounded-2xl p-5 md:p-6 ${wrap}`}>
      <div className="grid md:grid-cols-12 gap-5">
        {/* Flyer */}
        {item.flyer_url && (
          <div className="md:col-span-4">
            <img
              src={item.flyer_url}
              alt={`${item.title} flyer`}
              className="w-full rounded-xl shadow-sm border border-[var(--line)]"
              loading="lazy"
            />
          </div>
        )}

        <div className={item.flyer_url ? "md:col-span-8" : "md:col-span-12"}>
          <div className="flex items-start gap-4">
            <div className="shrink-0 mt-1">
              {item.canceled ? (
                <AlertCircle size={20} className="text-[var(--honey)]" />
              ) : (
                <Clock size={20} className="text-[var(--blue-deep)]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              {item.highlight && (
                <span className="inline-block font-mono-accent text-xs uppercase tracking-widest text-[var(--blue-deep)] bg-white px-2.5 py-1 rounded-full mb-2">
                  Featured this week
                </span>
              )}
              <h3
                className={`font-display text-xl md:text-2xl ${
                  item.canceled ? "text-[var(--ink-soft)] line-through" : "text-[var(--blue-ink)]"
                }`}
              >
                {item.title}
              </h3>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[var(--ink-soft)]">
                {time && (
                  <span className={item.canceled ? "text-[var(--honey)] font-semibold" : "font-medium"}>
                    {time}
                  </span>
                )}
                {item.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin size={13} /> {item.location}
                  </span>
                )}
              </div>
              {item.description && (
                <p className="mt-3 text-sm text-[var(--ink-soft)] leading-relaxed">
                  {item.description}
                </p>
              )}

              {(item.rsvp_url || item.payment_url || item.details_url) && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {item.rsvp_url && (
                    <a
                      href={item.rsvp_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-[var(--blue)] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[var(--blue-deep)] transition-colors"
                      data-testid="weekly-rsvp"
                    >
                      RSVP <ExternalLink size={12} />
                    </a>
                  )}
                  {item.payment_url && (
                    <a
                      href={item.payment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-[var(--honey)] text-[var(--blue-ink)] px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#EFA72A] transition-colors"
                      data-testid="weekly-pay"
                    >
                      Register / Pay <ExternalLink size={12} />
                    </a>
                  )}
                  {item.details_url && (
                    <a
                      href={item.details_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 border border-[var(--blue)] text-[var(--blue-deep)] px-4 py-2 rounded-full text-sm font-semibold hover:bg-[var(--blue)] hover:text-white transition-colors"
                    >
                      <FileText size={12} /> Full details
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
